import { useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { PlusButton } from "@/components/design/PlusButton";
import { RequiredBadge } from "@/components/design/EntryFields";
import { InlineEdit } from "@/components/design/InlineEdit";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { usePendingScope } from "@/lib/pending-edits";
import { LocalizedError } from "@/lib/localized-error";
import { AgentColumn } from "@/components/match/AgentDetail";
import { AgentMonogram } from "@/components/match/AgentMonogram";
import type { Agent } from "@/components/match/agents";
import {
  EMPTY_AGENT_DRAFT,
  EMPTY_LOCALIZED,
  agentToDraft,
  draftProblem,
  draftSignature,
} from "./agent-draft";
import type { AgentDraft, AgentDraftProblem, LocalizedDraft } from "./agent-draft";

interface AgentEditorProps {
  /** null = the create form. */
  agent: Agent | null;
  /** Persists the draft; rejects with a LocalizedError the SaveBar can show. */
  onSave: (draft: AgentDraft) => Promise<void>;
  onDelete: () => Promise<void>;
}

type Language = keyof LocalizedDraft;

const PROBLEM_COPY: Record<AgentDraftProblem, { id: string; defaultMessage: string }> = {
  name: { id: "admin.agents.problem.name", defaultMessage: "The name is missing." },
  vendor: { id: "admin.agents.problem.vendor", defaultMessage: "The vendor is missing." },
  role: { id: "admin.agents.problem.role", defaultMessage: "The role is missing. One language is enough." },
  toolName: { id: "admin.agents.problem.toolName", defaultMessage: "Every tool needs a name." },
  toolDescription: {
    id: "admin.agents.problem.toolDescription",
    defaultMessage: "Every tool needs a description in at least one language.",
  },
  url: {
    id: "admin.agents.problem.url",
    defaultMessage: "The product page must be a web address (http or https, no spaces, at most 128 characters).",
  },
  controlCharacters: {
    id: "admin.agents.problem.controlCharacters",
    defaultMessage: "A field contains invisible control characters. Remove them and save again.",
  },
};

const blank = (value: string) => value.trim().length === 0;
const blankText = (text: LocalizedDraft) => blank(text.german) && blank(text.english);

// Prose grows with its text where the browser can size a field to its content; elsewhere it keeps
// InlineEdit's three rows and the native resize handle.
const PROSE = "block [field-sizing:content] supports-[field-sizing:content]:resize-none";

// One AI agent in the Stammdaten tab, drawn as the sheet `/a/:id` shows (AgentDetail) with every
// value editable in place, the way the company and profile pages edit: no second copy of the facts
// as a form. Saved through the SaveBar like every other settings editor (usePendingScope), so
// leaving the section, switching the tab or picking another agent asks first. A draft genesis would
// refuse is refused by the SaveBar with the reason; the sheet itself only marks a required field
// while it is blank. Prose is edited one language at a time through the switch in the header; a
// field empty in that language shows its ordinary placeholder, never the other language's text:
// a missing language is simply missing. Rendered with
// `key` = the agent id, so a new selection starts from that agent's stored state.
export function AgentEditor({ agent, onSave, onDelete }: AgentEditorProps) {
  const intl = useIntl();
  const t = (id: string, defaultMessage: string, values?: Record<string, string>) =>
    intl.formatMessage({ id, defaultMessage }, values);

  const serverDraft = useMemo(() => (agent ? agentToDraft(agent) : EMPTY_AGENT_DRAFT), [agent]);
  const serverSignature = draftSignature(serverDraft);
  const [draft, setDraft] = useState(serverDraft);
  const [syncedFrom, setSyncedFrom] = useState(serverSignature);
  const [language, setLanguage] = useState<Language>(intl.locale.startsWith("de") ? "german" : "english");
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const saving = useRef<Promise<void> | null>(null);

  // A refetch after a save delivers the stored row; adopt it, as TeamProfileCard does, during render.
  if (serverSignature !== syncedFrom) {
    setDraft(serverDraft);
    setSyncedFrom(serverSignature);
  }

  const dirty = draftSignature(draft) !== syncedFrom;

  usePendingScope(`agent-editor:${agent?.id ?? "new"}`, {
    dirty,
    save: () => {
      // The SaveBar and the leave dialog can both ask while a write is in flight.
      if (saving.current) return saving.current;
      const problem = draftProblem(draft);
      if (problem) {
        return Promise.reject(new LocalizedError(t(PROBLEM_COPY[problem].id, PROBLEM_COPY[problem].defaultMessage)));
      }
      const promise = onSave(draft).finally(() => {
        saving.current = null;
      });
      saving.current = promise;
      return promise;
    },
    discard: () => setDraft(serverDraft),
  });

  const set = (patch: Partial<AgentDraft>) => setDraft((current) => ({ ...current, ...patch }));
  const title = agent ? agent.name : t("admin.agents.new", "New AI agent");
  const removeLabel = t("admin.agents.removeRow", "Remove row");
  const addLabel = t("match.agent.add", "Add");
  const languageLabel = {
    german: t("admin.agents.german", "German"),
    english: t("admin.agents.english", "English"),
  };

  const nameLabel = t("admin.agents.name", "Name");
  const vendorLabel = t("admin.agents.vendor", "Vendor");
  const urlLabel = t("admin.agents.url", "Product page");
  const modelLabel = t("admin.agents.model", "Model");
  const roleLabel = t("admin.agents.role", "Role");
  const coversLabel = t("match.agent.covers", "Covers");
  const toolsLabel = t("match.agent.tools", "Tools");
  const toolNameLabel = t("admin.agents.toolName", "Tool name");
  const toolDescriptionLabel = t("admin.agents.toolDescription", "Description");
  const integrationsLabel = t("match.agent.integrations", "Integrations");
  const guardrailsLabel = t("match.agent.guardrails", "Guardrails");

  const removeButton = (onClick: () => void, className = "") => (
    <Button variant="icon" size="sm" onClick={onClick} aria-label={removeLabel} title={removeLabel} className={className}>
      <span className="block -translate-y-px text-smycles-title leading-none">×</span> {/* i18n-allow */}
    </Button>
  );

  // The active language of one prose value. Its accessible name keeps the language, so a screen
  // reader hears which half it edits.
  const prose = (
    value: LocalizedDraft,
    onChange: (next: LocalizedDraft) => void,
    label: string,
    { multiline = true, className = "" }: { multiline?: boolean; className?: string } = {},
  ) => {
    const field = (
      <InlineEdit
        ariaLabel={`${label} (${languageLabel[language]})`}
        value={value[language]}
        onChange={(next) => onChange({ ...value, [language]: next })}
        placeholder={label}
        multiline={multiline}
        clearable={false}
        className={multiline ? `${PROSE} ${className}` : className}
      />
    );
    // A multiline InlineEdit pads its text without cancelling the padding as the single-line one
    // does; the negative margin puts its text on the same edge as the names above it.
    return multiline ? <div className="-mx-1.5">{field}</div> : field;
  };

  const updateList = <K extends "covers" | "guardrails">(key: K, i: number, next: LocalizedDraft) =>
    set({ [key]: draft[key].map((each, j) => (j === i ? next : each)) } as Partial<AgentDraft>);
  const removeFrom = <K extends "covers" | "tools" | "integrations" | "guardrails">(key: K, i: number) =>
    set({ [key]: (draft[key] as unknown[]).filter((_, j) => j !== i) } as Partial<AgentDraft>);
  const add = (label: string, onClick: () => void) => (
    <PlusButton label={addLabel} ariaLabel={`${label}: ${addLabel}`} onClick={onClick} />
  );

  const name = agent?.name ?? "";
  const chip = "box-flat inline-flex max-w-full items-center gap-1 py-0.5 pl-2.5 pr-0.5 text-smycles-label text-ink";

  return (
    <>
      <section className="@container box min-w-0 bg-surface p-5" aria-label={title}>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-3 border-b border-ink/10 pb-5">
          <AgentMonogram name={draft.name} />
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline">
              <InlineEdit
                ariaLabel={nameLabel}
                value={draft.name}
                onChange={(next) => set({ name: next })}
                placeholder={nameLabel}
                wrap
                className="font-serif text-smycles-title leading-tight"
              />
              {blank(draft.name) ? <RequiredBadge /> : null}
            </div>
            {/* Product tokens, deliberately untranslated. */}
            <div className="mt-1 flex flex-wrap items-baseline gap-x-1 text-smycles-meta text-ink-muted">
              <InlineEdit
                ariaLabel={vendorLabel}
                value={draft.vendor}
                onChange={(next) => set({ vendor: next })}
                placeholder={vendorLabel}
                clearable={false}
              />
              {blank(draft.vendor) ? <RequiredBadge /> : null}
              <span aria-hidden>/</span> {/* i18n-allow */}
              <InlineEdit
                ariaLabel={modelLabel}
                value={draft.model}
                onChange={(next) => set({ model: next })}
                placeholder={modelLabel}
                clearable={false}
              />
            </div>
            <div className="text-smycles-meta text-ink-muted">
              <InlineEdit
                ariaLabel={urlLabel}
                value={draft.url}
                onChange={(next) => set({ url: next })}
                placeholder={urlLabel}
                clearable={false}
              />
            </div>
          </div>
          {/* Below @sm the controls take their own line under the name rather than squeezing it. */}
          <div className="flex basis-full items-center justify-between gap-3 @sm:basis-auto @sm:justify-end">
            <SegmentedControl
              size="sm"
              value={language}
              onChange={setLanguage}
              options={[
                { value: "german", label: languageLabel.german },
                { value: "english", label: languageLabel.english },
              ]}
            />
            {agent ? (
              <Button variant="ghost" size="sm" disabled={deleting} onClick={() => setConfirmingDelete(true)}>
                {t("admin.agents.delete", "Delete AI agent")}
              </Button>
            ) : null}
          </div>
        </div>

        <div className="mt-6 flex items-start border-l-2 border-accent-decoration pl-4">
          <div className="min-w-0 flex-1">
            {prose(draft.role, (role) => set({ role }), roleLabel, {
              className: "text-smycles-body leading-relaxed",
            })}
          </div>
          {blankText(draft.role) ? <RequiredBadge /> : null}
        </div>

        <div className="mt-7">
          <AgentColumn label={coversLabel} count={draft.covers.length}>
            <div className="flex flex-wrap items-center gap-2">
              {draft.covers.map((cover, i) => (
                <span key={i} className={`${chip} bg-page`}>
                  {prose(cover, (next) => updateList("covers", i, next), coversLabel, { multiline: false })}
                  {removeButton(() => removeFrom("covers", i), "-my-1.5")}
                </span>
              ))}
              {add(coversLabel, () => set({ covers: [...draft.covers, EMPTY_LOCALIZED] }))}
            </div>
          </AgentColumn>
        </div>

        {/* AgentDetail's two columns, split earlier: its @2xl is 96rem (UI_CONVENTIONS, "max-w-2xl is
            96rem"), which the detail column of the master-detail never reaches. */}
        <div className="mt-7 grid grid-cols-1 gap-x-12 gap-y-8 border-t border-ink/10 pt-6 @[40rem]:grid-cols-2">
          <AgentColumn label={toolsLabel} count={draft.tools.length}>
            <ul className="space-y-3">
              {draft.tools.map((tool, i) => {
                const update = (patch: Partial<typeof tool>) =>
                  set({ tools: draft.tools.map((each, j) => (j === i ? { ...each, ...patch } : each)) });
                return (
                  <li key={i} className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline font-mono text-smycles-meta text-ink">
                        <InlineEdit
                          ariaLabel={toolNameLabel}
                          value={tool.name}
                          onChange={(next) => update({ name: next })}
                          placeholder={toolNameLabel}
                          clearable={false}
                        />
                        {blank(tool.name) ? <RequiredBadge /> : null}
                      </div>
                      <div className="flex items-start text-smycles-label leading-snug text-ink-muted">
                        <div className="min-w-0 flex-1">
                          {prose(tool.description, (description) => update({ description }), toolDescriptionLabel)}
                        </div>
                        {blankText(tool.description) ? <RequiredBadge /> : null}
                      </div>
                    </div>
                    {removeButton(() => removeFrom("tools", i))}
                  </li>
                );
              })}
            </ul>
            <div className={draft.tools.length > 0 ? "mt-3" : ""}>
              {add(toolsLabel, () => set({ tools: [...draft.tools, { name: "", description: EMPTY_LOCALIZED }] }))}
            </div>
          </AgentColumn>

          <div className="space-y-8">
            {/* Integrations are product names: one value, no language, no mono face. */}
            <AgentColumn label={integrationsLabel} count={draft.integrations.length}>
              <div className="flex flex-wrap items-center gap-2">
                {draft.integrations.map((value, i) => (
                  <span key={i} className={chip}>
                    <InlineEdit
                      ariaLabel={integrationsLabel}
                      value={value}
                      onChange={(next) =>
                        set({ integrations: draft.integrations.map((each, j) => (j === i ? next : each)) })
                      }
                      placeholder={integrationsLabel}
                      clearable={false}
                    />
                    {removeButton(() => removeFrom("integrations", i), "-my-1.5")}
                  </span>
                ))}
                {add(integrationsLabel, () => set({ integrations: [...draft.integrations, ""] }))}
              </div>
            </AgentColumn>

            <AgentColumn label={guardrailsLabel} count={draft.guardrails.length}>
              <ol className="space-y-2">
                {draft.guardrails.map((guardrail, i) => (
                  <li key={i} className="flex items-start gap-3 text-smycles-label leading-snug text-ink">
                    <span className="pt-0.5 font-mono text-ink-accent tabular-nums">{String(i + 1).padStart(2, "0")}</span>
                    <div className="min-w-0 flex-1">
                      {prose(guardrail, (next) => updateList("guardrails", i, next), guardrailsLabel)}
                    </div>
                    {removeButton(() => removeFrom("guardrails", i), "-mt-1")}
                  </li>
                ))}
              </ol>
              <div className={draft.guardrails.length > 0 ? "mt-3" : ""}>
                {add(guardrailsLabel, () => set({ guardrails: [...draft.guardrails, EMPTY_LOCALIZED] }))}
              </div>
            </AgentColumn>
          </div>
        </div>
      </section>

      <ConfirmDialog
        open={confirmingDelete}
        title={t("admin.agents.confirmDelete.title", "Delete “{name}”?", { name })}
        message={t(
          "admin.agents.confirmDelete.message",
          "The AI agent is removed for good and is no longer offered in matches. Matches that already name it keep their saved summary.",
        )}
        confirmLabel={t("admin.agents.delete", "Delete AI agent")}
        onConfirm={async () => {
          setConfirmingDelete(false);
          setDeleting(true);
          try {
            await onDelete();
          } finally {
            setDeleting(false);
          }
        }}
        onCancel={() => setConfirmingDelete(false)}
      />
    </>
  );
}
